import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createSeriesMarkers,
} from "lightweight-charts";
import { parseDateValue } from "../lib/datetime";

const OVERLAY_LINE_STYLES = {
  solid: LineStyle.Solid,
  dotted: LineStyle.Dotted,
  dashed: LineStyle.Dashed,
  largeDashed: LineStyle.LargeDashed,
  sparseDotted: LineStyle.SparseDotted,
};

const overlayLineStyle = (value) =>
  OVERLAY_LINE_STYLES[value] ?? LineStyle.Solid;


/**
 * CandlestickChart
 * - Stable chart instance (created once)
 * - Candles update incrementally when possible (no full rebuild flicker)
 * - Indicators update in their own effect (does not re-trigger candle setData)
 *
 * Props compatibility:
 * - accepts either `data` or `candles`
 */
const CandlestickChart = ({
  data,
  candles,
  indicators = {},
  overlays = [],
  markers = [],
  priceLines = [],
  onBadgeClose,
  showVolume = false,
  timeZone = "America/New_York",
}) => {
  const chartContainerRef = useRef(null);

  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const smaLineRef = useRef(null);
  const emaLineRef = useRef(null);
  const overlaySeriesRefs = useRef(new Map());
  const overlaySignaturesRef = useRef(new Map());
  const priceLineRefs = useRef(new Map());
  const priceLineSignaturesRef = useRef(new Map());
  const markersRef = useRef(null);
  const indicatorSignaturesRef = useRef({ sma: "", ema: "" });
  const markersSignatureRef = useRef("");

  // Used to prevent unnecessary resets
  const prevSnapshotRef = useRef(null); // { firstTime, length, lastTime, lastClose }
  const hasAutoFittedRef = useRef(false);
  const fitFrameRef = useRef(null);
  const badgeFrameRef = useRef(null);

  const [chartReady, setChartReady] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [floatingPriceBadges, setFloatingPriceBadges] = useState([]);

  // Prefer `candles` if provided; otherwise `data`
  const candleInput = candles ?? data ?? [];

  const scheduleFitContent = () => {
    if (!chartRef.current) return;

    if (fitFrameRef.current) {
      cancelAnimationFrame(fitFrameRef.current);
      fitFrameRef.current = null;
    }

    fitFrameRef.current = requestAnimationFrame(() => {
      fitFrameRef.current = requestAnimationFrame(() => {
        fitFrameRef.current = null;

        const container = chartContainerRef.current;
        if (!chartRef.current || !container) return;
        if (container.clientWidth <= 0 || container.clientHeight <= 0) return;

        chartRef.current.timeScale().fitContent();
        hasAutoFittedRef.current = true;
      });
    });
  };

  const clearOverlaySeries = (chartInstance = chartRef.current) => {
    if (!overlaySeriesRefs.current.size) {
      return;
    }

    overlaySeriesRefs.current.forEach((entry) => {
      const series = entry?.series ?? entry;
      const ownerChart = entry?.chart ?? chartInstance;

      if (!series || !chartInstance || ownerChart !== chartInstance) {
        return;
      }

      try {
        if (typeof chartInstance.removeSeries === "function") {
          chartInstance.removeSeries(series);
        }
      } catch (error) {
        console.warn("Failed to remove chart overlay series", error);
      }
    });

    overlaySeriesRefs.current = new Map();
    overlaySignaturesRef.current = new Map();
  };

  const parseChartTime = (rawTs) => {
    if (rawTs == null) return null;

    if (typeof rawTs === "number") {
      return rawTs > 1e12 ? Math.floor(rawTs / 1000) : Math.floor(rawTs);
    }

    if (typeof rawTs === "string") {
      const trimmed = rawTs.trim();
      if (/^\d+$/.test(trimmed)) {
        const numeric = Number(trimmed);
        return numeric > 1e12 ? Math.floor(numeric / 1000) : Math.floor(numeric);
      }
    }

    const ms = parseDateValue(rawTs)?.getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  };

  const normalizeLinePoints = (points) => {
    if (!Array.isArray(points) || points.length === 0) return [];
    const dedup = new Map();
    points.forEach((point) => {
      const time = parseChartTime(point?.time);
      const value = Number(point?.value);
      if (time == null || !Number.isFinite(value)) return;
      dedup.set(time, { time, value });
    });
    return Array.from(dedup.values()).sort((left, right) => left.time - right.time);
  };

  const formatChartDate = (unixSeconds, options) => {
    const date = new Date(unixSeconds * 1000);
    return date.toLocaleString("en-US", {
      hour12: false,
      timeZone,
      ...options,
    });
  };

  const formatCrosshairTime = (unixSeconds) => {
    return formatChartDate(unixSeconds, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTickMark = (timeValue) => {
    const unixSeconds = typeof timeValue === "number"
      ? timeValue
      : (
          typeof timeValue?.timestamp === "number"
            ? timeValue.timestamp
            : null
        );

    if (!Number.isFinite(unixSeconds)) {
      return "";
    }

    const localized = new Date(unixSeconds * 1000).toLocaleString("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const [, timePart] = localized.split(", ");

    if (!timePart) {
      return formatChartDate(unixSeconds, {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    if (timePart.startsWith("00:")) {
      return formatChartDate(unixSeconds, {
        month: "short",
        day: "numeric",
      });
    }

    return timePart;
  };

  // ---- Create chart ONCE (and only re-create if showVolume toggles) ----
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Cleanup previous chart if re-created (only when showVolume changes)
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      smaLineRef.current = null;
      emaLineRef.current = null;
      hasAutoFittedRef.current = false;
      prevSnapshotRef.current = null;
      overlaySeriesRefs.current = new Map();
      overlaySignaturesRef.current = new Map();
      priceLineRefs.current = new Map();
      priceLineSignaturesRef.current = new Map();
      markersRef.current = null;
      indicatorSignaturesRef.current = { sma: "", ema: "" };
      markersSignatureRef.current = "";
      setChartReady(false);
      if (fitFrameRef.current) {
        cancelAnimationFrame(fitFrameRef.current);
        fitFrameRef.current = null;
      }
    }

    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      localization: {
        locale: "en-US",
        timeFormatter: formatCrosshairTime,
      },
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#a1a1aa",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "#262626" },
        horzLines: { color: "#262626" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: "#262626",
        tickMarkFormatter: formatTickMark,
      },
      rightPriceScale: {
        borderColor: "#262626",
        scaleMargins: { top: 0.1, bottom: 0.2 },
        autoScale: true,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    const addSeriesCompat = (legacyMethod, seriesCtor, options) => {
      // lightweight-charts <=4 API
      if (typeof chart[legacyMethod] === "function") {
        return chart[legacyMethod](options);
      }
      // lightweight-charts >=5 API
      if (typeof chart.addSeries === "function" && seriesCtor) {
        return chart.addSeries(seriesCtor, options);
      }
      throw new Error(`Chart API missing ${legacyMethod}/addSeries`);
    };

    const candleSeries = addSeriesCompat("addCandlestickSeries", CandlestickSeries, {
      upColor: "#22c55e",
      downColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
      borderVisible: false,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    candleSeriesRef.current = candleSeries;
    markersRef.current = createSeriesMarkers(candleSeries, []);

    if (showVolume) {
      const volSeries = addSeriesCompat("addHistogramSeries", HistogramSeries, {
        priceFormat: { type: "volume" },
        priceScaleId: "vol",
      });

      chart.priceScale("vol").applyOptions({
        scaleMargins: { top: 0.82, bottom: 0.02 },
      });

      volumeSeriesRef.current = volSeries;
    }

    // Optional overlay lines
    smaLineRef.current = addSeriesCompat("addLineSeries", LineSeries, {
      lineWidth: 1,
      color: "#60a5fa",
    });
    emaLineRef.current = addSeriesCompat("addLineSeries", LineSeries, {
      lineWidth: 1,
      color: "#f59e0b",
    });

    // Tooltip / crosshair sync
    chart.subscribeCrosshairMove((param) => {
      if (!param?.time || !param?.seriesData) {
        setTooltip(null);
        return;
      }

      const bar = param.seriesData.get(candleSeriesRef.current);
      if (!bar) {
        setTooltip(null);
        return;
      }

      setTooltip({
        time: param.time,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      });
    });

    chartRef.current = chart;
    setChartReady(true);

    return () => {
      setChartReady(false);
      if (fitFrameRef.current) {
        cancelAnimationFrame(fitFrameRef.current);
        fitFrameRef.current = null;
      }
      if (badgeFrameRef.current) {
        cancelAnimationFrame(badgeFrameRef.current);
        badgeFrameRef.current = null;
      }
      setFloatingPriceBadges([]);
      chart.remove();
      chartRef.current = null;
    };
  }, [showVolume, timeZone]); // chart re-created when timezone changes

  // ---- Helper: format incoming candles to lightweight-charts format ----
  const formattedCandles = useMemo(() => {
    if (!candleInput || candleInput.length === 0) return [];

    const mapped = candleInput.map((c, index) => {
      const parsed = parseChartTime(c.timestamp);
      const fallback = Math.floor(Date.now() / 1000) - (candleInput.length - index) * 300;
      return {
        time: parsed ?? fallback,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
        volume: c.volume != null ? Number(c.volume) : undefined,
      };
    });

    // Drop invalid bars and de-duplicate by time (keep latest bar for each timestamp)
    const dedup = new Map();
    for (const b of mapped) {
      if (!Number.isFinite(b.time)) continue;
      if (![b.open, b.high, b.low, b.close].every(Number.isFinite)) continue;
      dedup.set(b.time, b);
    }

    const out = Array.from(dedup.values()).sort((a, b) => a.time - b.time);
    return out;
  }, [candleInput]);

  const candleTimes = useMemo(
    () => new Set(formattedCandles.map((candle) => candle.time)),
    [formattedCandles],
  );

  const normalizedSma = useMemo(() => normalizeLinePoints(indicators?.sma), [indicators?.sma]);
  const normalizedEma = useMemo(() => normalizeLinePoints(indicators?.ema), [indicators?.ema]);
  const normalizedOverlays = useMemo(
    () =>
      (overlays || [])
        .map((overlay, index) => {
          const values = normalizeLinePoints(overlay?.values);
          if (!values.length) return null;
          const key =
            overlay?.id ||
            overlay?.label ||
            overlay?.name ||
            `${overlay?.color || "#60a5fa"}-${index}`;
          return {
            key,
            label: overlay?.label || overlay?.name || key,
            color: overlay?.color || "#60a5fa",
            lineWidth: Number.isFinite(Number(overlay?.lineWidth)) ? Number(overlay.lineWidth) : 1.5,
            affectsScale: overlay?.affectsScale !== false,
            lastValueVisible: Boolean(overlay?.lastValueVisible),
            priceLineVisible: Boolean(overlay?.priceLineVisible),
            values,
            signature: JSON.stringify({
              values,
              color: overlay?.color || "#60a5fa",
              lineWidth: Number.isFinite(Number(overlay?.lineWidth)) ? Number(overlay.lineWidth) : 1.5,
              affectsScale: overlay?.affectsScale !== false,
              lastValueVisible: Boolean(overlay?.lastValueVisible),
              priceLineVisible: Boolean(overlay?.priceLineVisible),
            }),
          };
        })
        .filter(Boolean),
    [overlays],
  );
  const normalizedMarkers = useMemo(
    () =>
      Array.from(
        new Map(
          (markers || [])
            .map((marker, index) => {
              const parsedTime = parseChartTime(marker?.time);
              if (parsedTime == null || !candleTimes.has(parsedTime)) {
                return null;
              }
              return [
                marker.id || `${parsedTime}-${marker.position || "belowBar"}-${marker.text || ""}-${index}`,
                {
                  time: parsedTime,
                  position: marker.position || "belowBar",
                  shape: marker.shape || "circle",
                  color: marker.color || "#60a5fa",
                  text: marker.text || "",
                },
              ];
            })
            .filter(Boolean),
        ).values(),
      ).sort((left, right) => left.time - right.time),
    [markers, candleTimes],
  );
  const normalizedMarkersSignature = useMemo(
    () => JSON.stringify(normalizedMarkers),
    [normalizedMarkers],
  );
  const normalizedPriceLines = useMemo(
    () =>
      (priceLines || [])
        .map((line, index) => {
          const price = Number(line?.price);
          if (!Number.isFinite(price) || price <= 0) {
            return null;
          }

          let lineStyle = LineStyle.Dotted;
          if (line?.lineStyle === "solid") lineStyle = LineStyle.Solid;
          if (line?.lineStyle === "dashed") lineStyle = LineStyle.Dashed;
          if (line?.lineStyle === "largeDashed") lineStyle = LineStyle.LargeDashed;
          if (line?.lineStyle === "sparseDotted") lineStyle = LineStyle.SparseDotted;

          return {
            key: line?.id || `price-line-${index}`,
            price,
            color: line?.color || "#22d3ee",
            lineWidth: Number.isFinite(Number(line?.lineWidth)) ? Number(line.lineWidth) : 1,
            lineStyle,
            axisLabelVisible: line?.axisLabelVisible !== false,
            lineVisible: line?.lineVisible !== false,
            title: String(line?.title || "").trim(),
            badgeVisible: line?.badgeVisible !== false,
            badgePrimary: String(line?.badgePrimary || line?.title || "").trim(),
            badgeSecondary: String(line?.badgeSecondary || "").trim(),
            badgePnl: String(line?.badgePnl || line?.badgeSecondary || "").trim(),
            badgeQuantity: String(line?.badgeQuantity || "").trim(),
            badgeTrailing: String(line?.badgeTrailing || "").trim(),
            badgeTone: String(line?.badgeTone || "neutral").toLowerCase(),
            badgeQuantityTone: String(line?.badgeQuantityTone || "neutral").toLowerCase(),
            badgeTrailingTone: String(line?.badgeTrailingTone || "neutral").toLowerCase(),
            badgeAnchor: String(line?.badgeAnchor || "").trim().toLowerCase(),
            badgeRightInset: Number.isFinite(Number(line?.badgeRightInset))
              ? Number(line.badgeRightInset)
              : null,
            closeId: line?.closeId || null,
            signature: JSON.stringify({
              price,
              color: line?.color || "#22d3ee",
              lineWidth: Number.isFinite(Number(line?.lineWidth)) ? Number(line.lineWidth) : 1,
              lineStyle,
              axisLabelVisible: line?.axisLabelVisible !== false,
              lineVisible: line?.lineVisible !== false,
              title: String(line?.title || "").trim(),
            }),
          };
        })
        .filter(Boolean),
    [priceLines],
  );

  const layoutFloatingBadges = useCallback((badges, containerHeight) => {
    if (!badges.length || !Number.isFinite(containerHeight) || containerHeight <= 0) {
      return [];
    }

    const minGap = 28;
    const topPadding = 16;
    const bottomPadding = 16;
    const maxTop = Math.max(topPadding, containerHeight - bottomPadding);
    const ordered = badges
      .map((badge) => ({ ...badge }))
      .sort((left, right) => left.top - right.top);

    let previousTop = topPadding - minGap;
    ordered.forEach((badge) => {
      badge.top = Math.min(maxTop, Math.max(badge.top, previousTop + minGap));
      previousTop = badge.top;
    });

    for (let index = ordered.length - 1; index >= 0; index -= 1) {
      const nextBadge = ordered[index + 1];
      if (ordered[index].top > maxTop) {
        ordered[index].top = maxTop;
      }
      if (nextBadge && ordered[index].top > nextBadge.top - minGap) {
        ordered[index].top = Math.max(topPadding, nextBadge.top - minGap);
      }
    }

    return ordered.map((badge) => ({
      ...badge,
      top: Math.round(badge.top),
    }));
  }, []);

  const updateFloatingPriceBadges = useCallback(() => {
    const container = chartContainerRef.current;
    const candleSeries = candleSeriesRef.current;
    if (!container || !candleSeries || !normalizedPriceLines.length) {
      setFloatingPriceBadges([]);
      return;
    }

    const positioned = normalizedPriceLines
      .filter((line) => line.badgeVisible)
      .map((line) => {
        const coordinate = candleSeries.priceToCoordinate(line.price);
        if (!Number.isFinite(coordinate)) {
          return null;
        }
        const containerWidth = container.clientWidth || 0;
        const defaultLeft = Math.max(
          72,
          Math.min(
            Math.round(containerWidth * 0.54),
            Math.max(72, containerWidth - 192),
          ),
        );
        return {
          key: line.key,
          top: coordinate,
          left: line.badgeAnchor === "right"
            ? Math.max(72, containerWidth - (line.badgeRightInset || 192))
            : defaultLeft,
          color: line.color,
          lineVisible: line.lineVisible,
          lineWidth: line.lineWidth,
          badgePrimary: line.badgePrimary,
          badgeSecondary: line.badgeSecondary,
          badgePnl: line.badgePnl,
          badgeQuantity: line.badgeQuantity,
          badgeTrailing: line.badgeTrailing,
          badgeTone: line.badgeTone,
          badgeQuantityTone: line.badgeQuantityTone,
          badgeTrailingTone: line.badgeTrailingTone,
          closeId: line.closeId,
        };
      })
      .filter(Boolean);

    setFloatingPriceBadges(layoutFloatingBadges(positioned, container.clientHeight || 0));
  }, [layoutFloatingBadges, normalizedPriceLines]);

  const scheduleFloatingPriceBadges = useCallback(() => {
    if (badgeFrameRef.current) {
      cancelAnimationFrame(badgeFrameRef.current);
    }
    badgeFrameRef.current = requestAnimationFrame(() => {
      badgeFrameRef.current = null;
      updateFloatingPriceBadges();
    });
  }, [updateFloatingPriceBadges]);

  // ---- Candles effect (ONLY depends on candles/data) ----
  useEffect(() => {
    if (!chartReady || !candleSeriesRef.current || !chartRef.current) return;
    if (!formattedCandles || formattedCandles.length === 0) return;

    const firstTime = formattedCandles[0].time;
    const last = formattedCandles[formattedCandles.length - 1];
    const prev = prevSnapshotRef.current;

    // Determine incremental update vs full setData:
    // Incremental when:
    // - same firstTime
    // - length is same or +1
    // - last candle time is >= previous last time
    const canIncremental =
      hasAutoFittedRef.current &&
      prev &&
      prev.firstTime === firstTime &&
      formattedCandles.length >= prev.length &&
      formattedCandles.length <= prev.length + 1 &&
      last.time >= prev.lastTime;

    if (canIncremental) {
      // Update only the last bar (no flicker)
      candleSeriesRef.current.update(last);

      if (showVolume && volumeSeriesRef.current && last.volume != null) {
        volumeSeriesRef.current.update({
          time: last.time,
          value: last.volume,
          color: last.close >= last.open ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)",
        });
      }
    } else {
      // Full refresh (only when truly needed)
      candleSeriesRef.current.setData(formattedCandles);

      if (showVolume && volumeSeriesRef.current) {
        const vols = formattedCandles
          .filter((b) => b.volume != null)
          .map((b) => ({
            time: b.time,
            value: b.volume,
            color: b.close >= b.open ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)",
          }));
        volumeSeriesRef.current.setData(vols);
      }

      // Fit only on the initial load. Symbol/timeframe swaps already remount the
      // chart via its parent key, so re-fitting on every rolling window refresh
      // causes a visible flicker.
      if (!hasAutoFittedRef.current) {
        scheduleFitContent();
      }
    }

    // Snapshot
    prevSnapshotRef.current = {
      firstTime,
      length: formattedCandles.length,
      lastTime: last.time,
      lastClose: last.close,
    };
  }, [chartReady, formattedCandles, showVolume]);

  useEffect(() => {
    if (!chartReady) return;
    scheduleFloatingPriceBadges();
  }, [chartReady, formattedCandles, normalizedPriceLines, scheduleFloatingPriceBadges]);

  // ---- Indicators effect (separate so it DOES NOT trigger candle setData) ----
  useEffect(() => {
    if (!chartRef.current) return;
    if (smaLineRef.current) {
      const signature = JSON.stringify(normalizedSma);
      if (signature !== indicatorSignaturesRef.current.sma) {
        smaLineRef.current.setData(normalizedSma);
        indicatorSignaturesRef.current.sma = signature;
      }
    }

    if (emaLineRef.current) {
      const signature = JSON.stringify(normalizedEma);
      if (signature !== indicatorSignaturesRef.current.ema) {
        emaLineRef.current.setData(normalizedEma);
        indicatorSignaturesRef.current.ema = signature;
      }
    }
  }, [normalizedEma, normalizedSma]);

  useEffect(() => {
    const chartInstance = chartRef.current;
    if (!chartInstance) return;

    const addSeriesCompat = (legacyMethod, seriesCtor, options) => {
      if (typeof chartInstance[legacyMethod] === "function") {
        return chartInstance[legacyMethod](options);
      }
      return chartInstance.addSeries(seriesCtor, options);
    };

    const nextKeys = new Set(normalizedOverlays.map((overlay) => overlay.key));

    overlaySeriesRefs.current.forEach((entry, key) => {
      if (nextKeys.has(key)) return;
      try {
        chartInstance.removeSeries(entry.series);
      } catch (error) {
        console.warn("Failed to remove chart overlay series", error);
      }
      overlaySeriesRefs.current.delete(key);
      overlaySignaturesRef.current.delete(key);
    });

    normalizedOverlays.forEach((overlay) => {
      let entry = overlaySeriesRefs.current.get(overlay.key);
      const autoscaleInfoProvider =
        overlay.affectsScale === false ? () => null : undefined;
      if (!entry) {
        const series = addSeriesCompat("addLineSeries", LineSeries, {
          color: overlay.color,
          lineWidth: overlay.lineWidth,
          lineStyle: overlayLineStyle(overlay.lineStyle),
          crosshairMarkerVisible: false,
          lastValueVisible: overlay.lastValueVisible,
          priceLineVisible: overlay.priceLineVisible,
          autoscaleInfoProvider,
        });
        entry = {
          chart: chartInstance,
          series,
          color: overlay.color,
          lineWidth: overlay.lineWidth,
          affectsScale: overlay.affectsScale,
          lastValueVisible: overlay.lastValueVisible,
          priceLineVisible: overlay.priceLineVisible,
        };
        overlaySeriesRefs.current.set(overlay.key, entry);
      } else if (
        entry.color !== overlay.color
        || entry.lineWidth !== overlay.lineWidth
        || entry.affectsScale !== overlay.affectsScale
        || entry.lastValueVisible !== overlay.lastValueVisible
        || entry.priceLineVisible !== overlay.priceLineVisible
      ) {
        entry.series.applyOptions({
          color: overlay.color,
          lineWidth: overlay.lineWidth,
          lastValueVisible: overlay.lastValueVisible,
          priceLineVisible: overlay.priceLineVisible,
          autoscaleInfoProvider,
        });
        entry.color = overlay.color;
        entry.lineWidth = overlay.lineWidth;
        entry.affectsScale = overlay.affectsScale;
        entry.lastValueVisible = overlay.lastValueVisible;
        entry.priceLineVisible = overlay.priceLineVisible;
      }

      if (overlaySignaturesRef.current.get(overlay.key) !== overlay.signature) {
        entry.series.setData(overlay.values);
        overlaySignaturesRef.current.set(overlay.key, overlay.signature);
      }
    });
  }, [normalizedOverlays]);

  useEffect(() => {
    if (!markersRef.current) return;
    if (markersSignatureRef.current === normalizedMarkersSignature) {
      return;
    }
    markersRef.current.setMarkers(normalizedMarkers);
    markersSignatureRef.current = normalizedMarkersSignature;
  }, [normalizedMarkers, normalizedMarkersSignature]);

  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    if (!candleSeries) return;

    const nextKeys = new Set(normalizedPriceLines.map((line) => line.key));

    priceLineRefs.current.forEach((priceLine, key) => {
      if (nextKeys.has(key)) return;
      try {
        candleSeries.removePriceLine(priceLine);
      } catch (error) {
        console.warn("Failed to remove chart price line", error);
      }
      priceLineRefs.current.delete(key);
      priceLineSignaturesRef.current.delete(key);
    });

    normalizedPriceLines.forEach((line) => {
      const options = {
        price: line.price,
        color: line.color,
        lineWidth: line.lineWidth,
        lineStyle: line.lineStyle,
        axisLabelVisible: line.axisLabelVisible,
        lineVisible: line.lineVisible,
        title: line.title,
      };

      const existing = priceLineRefs.current.get(line.key);
      if (!existing) {
        try {
          const created = candleSeries.createPriceLine(options);
          priceLineRefs.current.set(line.key, created);
          priceLineSignaturesRef.current.set(line.key, line.signature);
        } catch (error) {
          console.warn("Failed to create chart price line", error);
        }
        return;
      }

      if (priceLineSignaturesRef.current.get(line.key) === line.signature) {
        return;
      }

      try {
        if (typeof existing.applyOptions === "function") {
          existing.applyOptions(options);
          priceLineSignaturesRef.current.set(line.key, line.signature);
          return;
        }
      } catch (error) {
        console.warn("Failed to update chart price line", error);
      }

      try {
        candleSeries.removePriceLine(existing);
      } catch (error) {
        console.warn("Failed to replace chart price line", error);
      }

      try {
        const recreated = candleSeries.createPriceLine(options);
        priceLineRefs.current.set(line.key, recreated);
        priceLineSignaturesRef.current.set(line.key, line.signature);
      } catch (error) {
        console.warn("Failed to recreate chart price line", error);
      }
    });
  }, [normalizedPriceLines]);

  useEffect(() => {
    if (!chartReady || !chartRef.current) return undefined;

    const chart = chartRef.current;
    const timeScale = chart.timeScale();
    const handleRangeChange = () => scheduleFloatingPriceBadges();
    const handleSizeChange = () => scheduleFloatingPriceBadges();

    timeScale.subscribeVisibleLogicalRangeChange(handleRangeChange);
    if (typeof timeScale.subscribeSizeChange === "function") {
      timeScale.subscribeSizeChange(handleSizeChange);
    }
    window.addEventListener("resize", handleSizeChange);

    return () => {
      timeScale.unsubscribeVisibleLogicalRangeChange(handleRangeChange);
      if (typeof timeScale.unsubscribeSizeChange === "function") {
        timeScale.unsubscribeSizeChange(handleSizeChange);
      }
      window.removeEventListener("resize", handleSizeChange);
    };
  }, [chartReady, scheduleFloatingPriceBadges]);

  const handleResetView = () => {
    scheduleFitContent();
    scheduleFloatingPriceBadges();
  };

  return (
    <div className="relative w-full h-full">
      {/* Tooltip. Bounded away from the Reset view button: on narrow screens
          the four OHLC values grew wide enough to run underneath it. */}
      {tooltip && (
        <div className="absolute top-2 left-2 z-10 max-w-[calc(100%-7rem)] rounded-md border border-white/10 bg-black/60 px-2 py-1.5 text-[10px] font-mono text-white backdrop-blur sm:px-3 sm:py-2">
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 sm:gap-x-3">
            <span>O: {tooltip.open?.toFixed?.(2)}</span>
            <span>H: {tooltip.high?.toFixed?.(2)}</span>
            <span>L: {tooltip.low?.toFixed?.(2)}</span>
            <span>C: {tooltip.close?.toFixed?.(2)}</span>
          </div>
        </div>
      )}

      {/* Reset view button */}
      <button
        type="button"
        onClick={handleResetView}
        className="absolute top-2 right-2 z-10 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[10px] text-white/80 hover:text-white backdrop-blur"
      >
        Reset view
      </button>

	      {floatingPriceBadges.length ? (
	        <div className="pointer-events-none absolute inset-0 z-10">
	          {floatingPriceBadges.map((badge) => {
	            const toneColors = badge.badgeTone === "positive"
	              ? {
                  backgroundColor: "#118a09",
                  color: "#ffffff",
                }
              : badge.badgeTone === "negative"
                ? {
                    backgroundColor: "#ff1a1a",
                    color: "#ffffff",
                  }
                : {
	                  backgroundColor: "#0f172a",
	                  color: "#e2e8f0",
	                };
	            const quantityColors = badge.badgeQuantityTone === "positive"
	              ? {
	                  backgroundColor: "#39ff14",
	                  color: "#000000",
	                }
	              : badge.badgeQuantityTone === "negative"
	                ? {
	                    backgroundColor: "#ef4444",
	                    color: "#ffffff",
	                  }
	                : {
	                    backgroundColor: "#1f2937",
	                    color: "#e5e7eb",
	                  };
	            const trailingColors = badge.badgeTrailingTone === "positive"
	              ? {
	                  backgroundColor: "#39ff14",
	                  color: "#000000",
	                }
	              : badge.badgeTrailingTone === "negative"
	                ? {
	                    backgroundColor: "#ef4444",
	                    color: "#ffffff",
	                  }
	                : {
	                    backgroundColor: "#d1d5db",
	                    color: "#000000",
	                  };
	            const lineHeight = Math.max(1, Math.round(Number(badge.lineWidth) || 1));

            return (
              <div
                key={badge.key}
                className="absolute inset-x-0"
                style={{ top: `${badge.top}px` }}
              >
                {badge.lineVisible ? (
                  <div
                    className="absolute inset-x-0 -translate-y-1/2"
                    style={{
                      top: 0,
                      height: `${lineHeight}px`,
                      backgroundColor: badge.color,
                      boxShadow: `0 0 0 ${lineHeight}px ${badge.color}14`,
                      opacity: 0.95,
                    }}
                  />
                ) : null}
                <div
                  className="absolute -translate-y-1/2"
                  style={{ left: `${badge.left}px`, top: 0 }}
                >
                  <div
                    className="inline-flex overflow-hidden rounded-[2px] border border-black/90 shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
                  >
                    <div
                      className="min-w-[84px] px-2 py-[5px] text-[9px] font-bold leading-none tabular-nums"
                      style={toneColors}
	                    >
	                      {badge.badgePnl || badge.badgeSecondary || badge.badgePrimary}
	                    </div>
	                    {badge.badgeQuantity ? (
	                      <div
	                        className="min-w-[28px] border-l border-black/90 px-2 py-[5px] text-center text-[9px] font-bold leading-none tabular-nums"
	                        style={quantityColors}
	                      >
	                        {badge.badgeQuantity}
	                      </div>
	                    ) : null}
	                    {badge.badgeTrailing ? (
	                      badge.closeId && typeof onBadgeClose === "function" ? (
	                        <button
	                          type="button"
	                          onClick={(event) => {
	                            event.preventDefault();
	                            event.stopPropagation();
	                            onBadgeClose(badge.closeId);
	                          }}
	                          aria-label="Close position"
	                          title="Close position"
	                          className="pointer-events-auto min-w-[24px] cursor-pointer border-l border-black/90 px-2 py-[5px] text-center text-[9px] font-medium leading-none tabular-nums hover:brightness-110 focus:outline-none focus:ring-1 focus:ring-white/40"
	                          style={trailingColors}
	                        >
	                          {badge.badgeTrailing}
	                        </button>
	                      ) : (
	                        <div
	                          className="min-w-[24px] border-l border-black/90 px-2 py-[5px] text-center text-[9px] font-medium leading-none tabular-nums"
	                          style={trailingColors}
	                        >
	                          {badge.badgeTrailing}
	                        </div>
	                      )
	                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Chart container — autoSize:true makes the chart fill this div automatically */}
      <div ref={chartContainerRef} className="w-full h-full" />
    </div>
  );
};

export default React.memo(CandlestickChart);
